import type { StatusLevel } from '@/lib';

// One row of the FleetTable. Mirrors the shape of `S2Fleet.tsx::grid`
// items after the syntheticPeriods enrichment — only the columns the
// table renders are declared here.
export type FleetRow = {
  assetId: string;
  name: string;
  assetClass: string;
  siteName: string;
  currentHealthScore: number;
  currentHealthPrev?: number;
  openAlertCount: number;
  currentStatus: StatusLevel;
  rul_days?: number;
  last_seen?: string;
};

export type FleetColumnKey =
  | 'name'
  | 'site'
  | 'klass'
  | 'health'
  | 'rul'
  | 'severity'
  | 'open_alerts'
  | 'last_seen';

export type FleetColumn = {
  key: FleetColumnKey;
  label: string;
  defaultVisible: boolean;
  // Field used for both sort + CSV export.
  sortValue: (row: FleetRow) => string | number;
};

export const FLEET_COLUMNS: FleetColumn[] = [
  {
    key: 'name',
    label: 'Name',
    defaultVisible: true,
    sortValue: (r) => r.name.toLowerCase(),
  },
  {
    key: 'site',
    label: 'Site',
    defaultVisible: true,
    sortValue: (r) => r.siteName.toLowerCase(),
  },
  {
    key: 'klass',
    label: 'Class',
    defaultVisible: true,
    sortValue: (r) => r.assetClass.toLowerCase(),
  },
  {
    key: 'health',
    label: 'Health',
    defaultVisible: true,
    sortValue: (r) => r.currentHealthScore,
  },
  {
    key: 'rul',
    label: 'RUL',
    defaultVisible: true,
    // Push undefined RUL to the bottom of an ascending sort.
    sortValue: (r) => r.rul_days ?? Number.POSITIVE_INFINITY,
  },
  {
    key: 'severity',
    label: 'Severity',
    defaultVisible: true,
    sortValue: (r) => SEVERITY_ORDER[r.currentStatus] ?? 99,
  },
  {
    key: 'open_alerts',
    label: 'Open alerts',
    defaultVisible: true,
    sortValue: (r) => r.openAlertCount,
  },
  {
    key: 'last_seen',
    label: 'Last seen',
    defaultVisible: false,
    sortValue: (r) => r.last_seen ?? '',
  },
];

// Sort severity by operational priority, not alphabetically.
const SEVERITY_ORDER: Record<string, number> = {
  critical: 0,
  warn: 1,
  watch: 2,
  healthy: 3,
  info: 4,
};

export const COLUMN_VISIBILITY_LS_KEY = 'pdm.s2.cols';

export function loadColumnVisibility(): Record<FleetColumnKey, boolean> {
  const defaults = Object.fromEntries(
    FLEET_COLUMNS.map((c) => [c.key, c.defaultVisible]),
  ) as Record<FleetColumnKey, boolean>;
  if (typeof window === 'undefined') return defaults;
  try {
    const raw = window.localStorage.getItem(COLUMN_VISIBILITY_LS_KEY);
    if (!raw) return defaults;
    const parsed = JSON.parse(raw) as Record<string, boolean>;
    return { ...defaults, ...parsed } as Record<FleetColumnKey, boolean>;
  } catch {
    return defaults;
  }
}

export function saveColumnVisibility(vis: Record<FleetColumnKey, boolean>) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(COLUMN_VISIBILITY_LS_KEY, JSON.stringify(vis));
  } catch {
    // Swallow quota errors — table still works without persistence.
  }
}

// CSV row shape — always emits every column, regardless of visibility,
// so the export is reproducible across users with different toggles.
export function rowsToCSV(rows: FleetRow[]): string {
  const header = [
    'asset_id',
    'name',
    'site',
    'class',
    'health',
    'rul_days',
    'severity',
    'open_alerts',
  ];
  const escape = (v: string | number | undefined | null) =>
    JSON.stringify(v ?? '');
  const lines = rows.map((r) =>
    [
      escape(r.assetId),
      escape(r.name),
      escape(r.siteName),
      escape(r.assetClass),
      escape(r.currentHealthScore),
      escape(r.rul_days),
      escape(r.currentStatus),
      escape(r.openAlertCount),
    ].join(','),
  );
  return [header.join(','), ...lines].join('\n');
}
